import React, { Fragment, type ReactNode } from "react";
import { Heading } from "@purpur/heading";
import { Link } from "@purpur/link";
import { Paragraph } from "@purpur/paragraph";
import { TextSpacing } from "@purpur/text-spacing";

import { BLOCK, MARK } from "./constants";
import { tableRenderer } from "./renderers";
import type {
  ImageProp,
  LinkProp,
  Mark,
  NodeTypes,
  RichTextNode,
  RichTextProps,
  Value,
  VideoPosterProp,
} from "./types";
import { normalizeNode } from "./utils";

// Inspired by https://github.com/contentful/rich-text/blob/master/packages/rich-text-react-renderer/src/index.tsx
const MARK_TYPES = {
  [MARK.BOLD]: (children: ReactNode) => <b>{children}</b>,
  [MARK.ITALIC]: (children: ReactNode) => <i>{children}</i>,
  [MARK.UNDERLINE]: (children: ReactNode) => <u>{children}</u>,
  [MARK.CODE]: (children: ReactNode) => <code>{children}</code>,
};

const getNodeType = (node: RichTextNode) => {
  const type = node.type ?? node.nodeType!;
  if (type.startsWith("heading-")) return type.replace("heading-", "header-");
  if (type.startsWith("entry-hyperlink")) return type.replace("entry-hyperlink", "hyperlink");
  return type;
};

function getNodeTypes<T = Value>(
  link: LinkProp,
  image: ImageProp,
  videoPoster: VideoPosterProp
): NodeTypes<T> {
  return {
    [BLOCK.RICHTEXTDOCUMENT]: (_, children) => children,
    [BLOCK.DOCUMENT]: (_, children) => children,
    [BLOCK.PARAGRAPH]: (_, children) => <Paragraph variant="paragraph-200">{children}</Paragraph>,
    [BLOCK.TEXT]: (node) => {
      const { value: nodeValue, marks } = node;
      const value = typeof nodeValue === "string" ? nodeValue.replace(/\n/g, "<br />") : nodeValue;

      const element = <span dangerouslySetInnerHTML={{ __html: value as string }} />;

      if (!marks) {
        return element;
      }
      return marks.reduce((accumulator: ReactNode, currentValue: Mark | { type: Mark }) => {
        return MARK_TYPES[typeof currentValue === "string" ? currentValue : currentValue.type](
          accumulator
        );
      }, element);
    },
    [BLOCK.HYPERLINK]: (node, children) => {
      const uri = node.uri ?? (node.data?.uri as string);

      if (link) {
        return <Link variant="text">{link(children, uri)}</Link>;
      }
      return (
        <Link variant="text">
          <a href={uri}>{children}</a>
        </Link>
      );
    },
    [BLOCK.HR]: () => <hr />,
    [BLOCK.HEADER_2]: (_, children) => (
      <Heading tag="h2" variant="title-300">
        {children}
      </Heading>
    ),
    [BLOCK.HEADER_3]: (_, children) => (
      <Heading tag="h3" variant="title-200">
        {children}
      </Heading>
    ),
    [BLOCK.HEADER_4]: (_, children) => (
      <Heading tag="h4" variant="title-100">
        {children}
      </Heading>
    ),
    [BLOCK.HEADER_5]: (_, children) => (
      <Heading tag="h5" variant="title-100">
        {children}
      </Heading>
    ),
    [BLOCK.HEADER_6]: (_, children) => (
      <Heading tag="h6" variant="title-100">
        {children}
      </Heading>
    ),
    [BLOCK.TABLE]: tableRenderer,
    [BLOCK.TABLE_ROW]: (_, children) => <tr>{children}</tr>,
    [BLOCK.TABLE_HEADER_CELL]: (_, children) => <th scope="col">{children}</th>,
    [BLOCK.TABLE_CELL]: (_, children) => {
      return <td>{children}</td>;
    },
    [BLOCK.TABLE_BODY]: (_, children) => <tbody>{children}</tbody>,
    [BLOCK.LIST_ITEM]: (_, children) => <li>{children}</li>,
    [BLOCK.OL_LIST]: (_, children) => <ol>{children}</ol>,
    [BLOCK.UL_LIST]: (_, children) => <ul>{children}</ul>,
    [BLOCK.QUOTE]: (_, children) => <blockquote>&#8220;{children}&#8221;</blockquote>,
    [BLOCK.EMBEDDED_ENTRY]: (_, children) => children,
    [BLOCK.EMBEDDED_ASSET]: (_, children) => children,
    [BLOCK.IMAGE]: ({ value }) => {
      if (typeof value === "object" && value && "url" in value && value.url) {
        if (image) {
          return image(value.url, value.altText, value.image?.contentType);
        }
        return <img src={value.url} alt={value.altText ?? ""} loading="lazy" />;
      }
      return null;
    },
    [BLOCK.VIDEO]: ({ value }) => {
      if (typeof value === "object" && value && "posterImage" in value) {
        const posterImage = value.posterImage?.url;

        return (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            data-testid="rich text video"
            controls
            src={value.url}
            poster={videoPoster && posterImage ? videoPoster(posterImage) : posterImage}
          />
        );
      }
      return null;
    },
  };
}

const isValueWhitespaceOnly = (value: string): boolean => {
  return !value.replace(/\s+/g, "");
};

/**
 * Strictly checks if a singular node value OR nested node content is an empty element.
 */

const isNodeOrContentEmpty = <T,>(node: RichTextNode<T>): boolean => {
  const { content, value } = node;

  if (!content) {
    return typeof value === "string" && isValueWhitespaceOnly(value);
  }

  for (const item of content) {
    if (getNodeType(item) === BLOCK.HYPERLINK) {
      return false;
    }
  }

  return (
    content.length > 0 &&
    typeof content[0].value === "string" &&
    isValueWhitespaceOnly(content[0].value)
  );
};

const serializeRichText = <T,>(
  node: RichTextNode<T>,
  customNodeTypes?: NodeTypes<T>,
  link?: LinkProp,
  image?: ImageProp,
  videoPoster?: VideoPosterProp
): ReactNode => {
  let index = 0; // nodes in rich text does not contain ids, we'll use index as keys for react

  const nodes = {
    ...getNodeTypes(link, image, videoPoster),
    ...customNodeTypes,
  };

  const renderNode = (node: RichTextNode<T>): ReactNode => {
    const renderFunc = nodes[getNodeType(node)];

    // node types that are missing from both default NODE_TYPES and custom are never rendered
    if (typeof renderFunc === "undefined") {
      return null;
    }

    if (isNodeOrContentEmpty(node)) {
      return null;
    }

    const children = node.content ? node.content.map(renderNode) : [];

    return <Fragment key={index++}>{renderFunc(normalizeNode(node), children)}</Fragment>;
  };

  return renderNode(node);
};

/* Not using a arrow function here since the default generic will mess with the highlighting
  and it still needs a eslint-disable-next-line because it still lints incorrect */

function RichText<T = Value>({
  data,
  className,
  customNodeTypes,
  noTextSpacing = false,
  link,
  image,
  videoPoster,
  element: ElementTag = "div",
  ...rest
}: RichTextProps<T>) {
  return data ? (
    noTextSpacing ? (
      <ElementTag className={className} {...rest}>
        {serializeRichText(data, customNodeTypes, link, image, videoPoster)}
      </ElementTag>
    ) : (
      <TextSpacing className={className} element={ElementTag} {...rest}>
        {serializeRichText(data, customNodeTypes, link, image, videoPoster)}
      </TextSpacing>
    )
  ) : null;
}

export { getNodeTypes, RichText, serializeRichText };
